import "../assets/qreader.css";
import { useCallback, useState, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { axiosInstance } from "../utils/axios";
import { toast } from "react-toastify"; // Add toast for b
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import useDialogStore from "../stores/useDialogStore";
import dayjs from "dayjs";
export default function GetDeviceInfo() {
  const scannedRecentlyRef = useRef(false);
  const [scannerPaused, setScannerPaused] = useState(false);
  const isComponentMounted = useRef(true);
  const [deviceInfo, setDeviceInfo] = useState(null);

  const deviceLookupMutation = useMutation({
    mutationFn: async (scannedId) => {
      const { data } = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device/lookup?query=${scannedId}`
      );
      return data.data;
    },
    onSuccess: (deviceData) => {
      setDeviceInfo(deviceData);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Device not found");
      setScannerPaused(false);
      scannedRecentlyRef.current = false;
    },
  });

  const handleResult = useCallback(
    async (text, result) => {
      if (scannedRecentlyRef.current || !isComponentMounted) return;

      scannedRecentlyRef.current = true;
      if (!result) {
        toast.error("Invalid scan data");
        return;
      }

      const scannedIdValue = String(result).trim();
      console.log("scannedIdValue", scannedIdValue);
      deviceLookupMutation.mutate(scannedIdValue);
    },
    [isComponentMounted, deviceLookupMutation]
  );

  const handleError = useCallback(
    (error) => {
      if (isComponentMounted) {
        toast.error("Scanner error: " + error?.message, { autoClose: 3000 });
        console.error("Scanner error:", error);
      }
    },
    [isComponentMounted]
  );

  return (
    <div className="qreaderr">
      <Dialog.Title className="text-sm font-medium text-gray-900 mb-4">
        Please Scan the Device QR Code to Edit the Device
      </Dialog.Title>

      <Scanner
        onResult={handleResult}
        onError={handleError}
        constraints={{
          facingMode: "environment",
        }}
        paused={scannerPaused}
        className="w-full aspect-square"
      />

      <div className="mt-4 text-sm text-gray-500">
        Please align the QR code within the scanner frame
      </div>

      {deviceInfo && (
        <div className="mt-4 text-sm text-gray-500">
          <div className="text-sm font-medium text-gray-900 mb-4">
            Device Info
          </div>
          <div className="text-sm text-gray-700">
            <table className="min-w-full border border-gray-200 rounded overflow-hidden">
              <tbody>
                {[
                  { label: "Unique ID", value: deviceInfo.unique_id },
                  { label: "Serial No", value: deviceInfo.serial_no },
                  { label: "Brand", value: deviceInfo.brand_name },
                  { label: "Model", value: deviceInfo.model_name },
                  {
                    label: "Options",
                    value: Array.isArray(deviceInfo.options)
                      ? deviceInfo.options.length > 0
                        ? deviceInfo.options.join(", ")
                        : null
                      : deviceInfo.options,
                    isOptions: true, // custom flag for rendering
                  },
                  { label: "Counter Black", value: deviceInfo.counter_black },
                  { label: "Counter Color", value: deviceInfo.counter_color },
                  { label: "Total Counter", value: deviceInfo.total_counter },
                  { label: "Palette", value: deviceInfo.palette },
                  { label: "Stock", value: deviceInfo.stock },
                  { label: "User ID", value: deviceInfo.user_id },

                  ...(deviceInfo.status != null
                    ? [
                        { label: "Status", value: deviceInfo.status },
                        {
                          label: "Invoice Number",
                          value: deviceInfo.invoice_number,
                        },
                        { label: "Client ID", value: deviceInfo.client_id },
                        { label: "Client Name", value: deviceInfo.client_name },
                      ]
                    : []),
                  { label: "Comment", value: deviceInfo.comment },
                  ...(deviceInfo.recycled_date != null
                    ? [
                        {
                          label: "Recycled",
                          value: dayjs
                            .utc(deviceInfo.recycled_date)
                            .format("DD/MM/YYYY"),
                        },
                      ]
                    : []),
                  ...(deviceInfo.spare_parts != null
                    ? [
                        {
                          label: "Spare Parts",
                          value: dayjs
                            .utc(deviceInfo.spare_parts)
                            .format("DD/MM/YYYY"),
                        },
                      ]
                    : []),
                ].map(({ label, value, isOptions }) => {
                  // Determine color for Options row
                  let optionsBgClass = "";
                  let optionsTextClass = "";
                  if (isOptions) {
                    // Try to determine type from deviceInfo.type or deviceInfo.options_type
                    // Fallback to checking value for "green" or "blue"
                    const type =
                      deviceInfo.type ||
                      deviceInfo.options_type ||
                      (typeof value === "string"
                        ? value.toLowerCase().includes("green")
                          ? "green"
                          : value.toLowerCase().includes("blue")
                          ? "blue"
                          : ""
                        : "");
                    if (type === "green") {
                      optionsBgClass = "bg-green-100";
                      optionsTextClass = "text-green-700";
                    } else if (type === "blue") {
                      optionsBgClass = "bg-blue-100";
                      optionsTextClass = "text-blue-700";
                    }
                  }
                  return (
                    <tr key={label} className="border-b last:border-b-0">
                      <td
                        className={`py-2 px-3 font-semibold text-gray-900 bg-gray-50 w-40 ${
                          isOptions ? optionsBgClass : ""
                        }`}
                      >
                        {label}:
                      </td>
                      <td
                        className={`py-2 px-3 ${
                          isOptions
                            ? optionsBgClass + " " + optionsTextClass
                            : ""
                        }`}
                      >
                        {value ?? (
                          <span className="text-gray-400 italic">N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
